import { isReference } from '@apollo/client';
import type {
  ApolloQueryResult,
  FetchResult,
  StoreObject,
} from '@apollo/client';

import { isDevelopment } from '@constants/env';
import { VITAL_SIGN_TYPES } from '@constants/fhir';

import { useGetReferenceRanges } from '@hooks/api/reference_range';
import { useConversion } from '@hooks/useConversion';
import { useResponseHandler } from '@hooks/useResponseHandler';
import { useSession } from '@hooks/useSession';

import type { DataPointMapped } from '@models/data-point';
import type { ObservationRecord } from '@models/observation';
import { TrackerType, VitalSignObservationRecord } from '@models/vital-sign';

import { parseArgs, type CachedRecordList } from '@utils/apollo';
import { convertUnit, getUnit } from '@utils/conversion';
import { getDeviceBrandName } from '@utils/device-brand';
import { getHeightMetres } from '@utils/height';

import type { ListBodyObservationsQuery } from 'generated/body-observation.generated';
import type { ListFitnessObservationsQuery } from 'generated/fitness.generated';
import type { ListHealthScoreObservationsQuery } from 'generated/health-score.generated';
import type { ListSleepObservationsQuery } from 'generated/sleep.generated';
import type {
  GetObservationsModelInput,
  ReferenceRangeModel,
} from 'generated/types';
import {
  useDeleteVitalSignObservationRecordMutation,
  useGetVitalSignObservationQuery,
  useListVitalSignObservationsQuery,
  usePostVitalSignObservationRecordMutation,
  useUpdateVitalSignObservationRecordMutation,
  type DeleteVitalSignObservationRecordMutation,
  type DeleteVitalSignObservationRecordMutationVariables,
  type ListVitalSignObservationsQuery,
  type PostVitalSignObservationRecordMutationVariables,
  type UpdateVitalSignObservationRecordMutationVariables,
} from 'generated/vital-sign.generated';

import { useGetBodyObservations } from './body-observation';
import { useGetFitnessObservations } from './fitness-observation';
import { useGetHealthScoreObservations } from './health-score';
import { useGetSleepObservations } from './sleep-observation';

export const useGetVitalSigns = (
  code?: string,
  skipQuery?: boolean,
  filterUnpublished?: boolean,
  dateRange?: DateRange
) => {
  const { handleError } = useResponseHandler();
  const {
    state: { activeProfile },
  } = useSession();
  const patient = activeProfile?.patient_id ?? '';

  const response = useListVitalSignObservationsQuery({
    variables: {
      input: {
        patient,
        code,
        filterUnpublished,
        dateFrom: dateRange?.dateFrom,
        dateTo: dateRange?.dateTo,
      },
    },
    skip: !patient || Boolean(skipQuery),
  });
  const { data, error } = response;
  handleError(error);
  const records = (data?.listVitalSignObservations.data ?? []).map(
    (r) => new VitalSignObservationRecord(r)
  );
  return { ...response, data: records };
};

export const useGetVitalSign = (params: {
  record_id: string;
  skip?: boolean;
}) => {
  const { handleError } = useResponseHandler();
  const { record_id, skip } = params;
  const {
    state: { activeProfile },
  } = useSession();
  const patient_id = activeProfile?.patient_id ?? '';
  const response = useGetVitalSignObservationQuery({
    variables: {
      input: {
        patient_id,
        record_id,
      },
    },
    skip: skip || !patient_id || !record_id,
  });
  const { data, error } = response;
  handleError(error);
  const record = data?.getVitalSignObservation;
  return {
    ...response,
    data: record ? new VitalSignObservationRecord(record) : null,
  };
};

/**
 * Map observations onto the data points the charts and tables read.
 *
 * `source` is resolved to a brand display name here rather than at render
 * time, because the table filter builds its options from the raw record
 * values and would otherwise offer the lowercase provider slug while the
 * table body showed the brand name.
 * @param type Vital sign type code
 * @param data Observations to map
 * @param conversion Per-code unit conversion state
 * @returns Data points for the chart and table views
 */
export function getVitalSignResults(
  type: string,
  data: ObservationRecord[],
  conversion: Record<string, boolean | undefined>
) {
  const VITAL_SIGN_TYPE_DATA =
    type in VITAL_SIGN_TYPES ? VITAL_SIGN_TYPES[type] : undefined;

  const keys = (VITAL_SIGN_TYPE_DATA?.keys ?? [])
    .map((o) => Object.values(o))
    .flat()
    .reduce<string[]>((acc, curr) => {
      if (!acc.includes(curr)) {
        acc.push(curr);
      }
      return acc;
    }, []);
  const multiplier = VITAL_SIGN_TYPE_DATA?.multiplier ?? 1;
  const precision = VITAL_SIGN_TYPE_DATA?.precision ?? 1;
  const results: DataPointMapped[] = data
    .filter((d) => keys.some((k) => d.hasCode(k)))
    .map((d) => {
      if (d.component) {
        if (type === 'body-height') {
          // Normalise metres / feet+inches to metres, then convert once. NaN
          // for units we cannot read: the chart filters it and `format`
          // renders it as '- -', rather than showing a wrong height.
          const metres = getHeightMetres(d);
          return {
            record: d,
            id: d.id,
            value:
              metres === null
                ? NaN
                : Number(
                    convertUnit(
                      metres,
                      'm',
                      conversion[type] ? 'CON' : 'SI',
                      2,
                      type,
                      multiplier
                    )
                  ),
            unit: getUnit('m', conversion[type] ? 'CON' : 'SI', type),
            type: 'body-height',
            clinicalNonClinical: d.isClinical() ? 'Clinical' : 'Non-Clinical',
            date: d.getObservationDate(),
            source: getDeviceBrandName(d.source) ?? '-',
            curationStatus: d.curationStatus,
            recordSource: d.sourceType,
            alert: d.alert,
            alertDate: d.alert,
          };
        }
        return d.component.map((c) => {
          const q = c?.valueQuantity;
          return {
            record: d,
            value: Number(
              convertUnit(
                typeof q?.value === 'string' ? 0 : q?.value,
                q?.unit,
                conversion[type] ? 'CON' : 'SI',
                precision,
                type,
                multiplier
              )
            ),
            id: d.id,
            unit: getUnit(q?.unit, conversion[type] ? 'CON' : 'SI', type),
            clinicalNonClinical: d.isClinical() ? 'Clinical' : 'Non-Clinical',
            date: d.getObservationDate(),
            source: getDeviceBrandName(d.source) ?? '-',
            type:
              (VITAL_SIGN_TYPE_DATA?.keys ?? []).find((keys) => {
                return (
                  c?.code.coding?.some((o) => o.code === keys.common) ||
                  c?.code.coding?.some((o) => o.code === keys.validic) ||
                  c?.code.coding?.some((o) => o.code === keys.vitall) ||
                  c?.code.coding?.some((o) => o.code === keys.loinc) ||
                  c?.code.coding?.some((o) => o.code === keys.snomed) ||
                  c?.code.coding?.some((o) => o.code === keys.icd10)
                );
              })?.common ?? '',
            curationStatus: d.curationStatus,
            recordSource: d.sourceType,
            alert: d.alert,
            alertDate: d.alert,
          };
        });
      }
      return [
        {
          record: d,
          id: d.id,
          value:
            typeof d.value === 'boolean'
              ? 0
              : Number(
                  convertUnit(
                    typeof d.value === 'string' ? 0 : d.value,
                    d.unit,
                    conversion[type] ? 'CON' : 'SI',
                    precision,
                    type,
                    multiplier
                  )
                ),
          unit: getUnit(d.unit, conversion[type] ? 'CON' : 'SI', type),
          clinicalNonClinical: d.isClinical() ? 'Clinical' : 'Non-Clinical',
          date: d.getObservationDate(),
          source: getDeviceBrandName(d.source) ?? '-',
          type:
            (VITAL_SIGN_TYPE_DATA?.keys ?? []).find((keys) => {
              return (
                d.hasCode(keys.common) ||
                (keys.validic && d.hasCode(keys.validic)) ||
                (keys.vitall && d.hasCode(keys.vitall))
              );
            })?.common ?? '',
          curationStatus: d.curationStatus,
          recordSource: d.sourceType,
          alert: d.alert,
          alertDate: d.alert,
        },
      ];
    })
    .flat()
    .sort((a, b) => {
      if (!b.date) {
        return 1;
      }
      if (!a.date) {
        return -1;
      }
      return b.date.getTime() - a.date.getTime();
    });
  return results;
}

function useGetObservationsByCategory(
  category: TrackerType,
  code: string,
  dateRange?: DateRange
): {
  data: VitalSignObservationRecord[];
  loading: boolean;
  refetch: (variables?: {
    input: GetObservationsModelInput;
  }) => Promise<
    ApolloQueryResult<
      | ListVitalSignObservationsQuery
      | ListBodyObservationsQuery
      | ListFitnessObservationsQuery
      | ListHealthScoreObservationsQuery
      | ListSleepObservationsQuery
    >
  >;
} {
  const {
    data: vitalSignData,
    loading: vitalSignLoading,
    refetch: refetchVitalSign,
  } = useGetVitalSigns(
    code,
    category !== TrackerType.VitalSigns,
    undefined,
    dateRange
  );
  const {
    data: fitnessData,
    loading: fitnessLoading,
    refetch: refetchFitness,
  } = useGetFitnessObservations(
    code,
    category !== TrackerType.Fitness,
    undefined,
    dateRange
  );
  const {
    data: sleepData,
    loading: sleepLoading,
    refetch: refetchSleep,
  } = useGetSleepObservations(
    code,
    category !== TrackerType.Sleep,
    undefined,
    dateRange
  );
  const {
    data: bodyData,
    loading: bodyLoading,
    refetch: refetchBody,
  } = useGetBodyObservations(
    code,
    category !== TrackerType.Body,
    undefined,
    dateRange
  );
  const {
    data: healthScoreData,
    loading: healthScoresLoading,
    refetch: refetchHealthScores,
  } = useGetHealthScoreObservations(
    code,
    category !== TrackerType.HealthScore,
    dateRange
  );

  return {
    data: vitalSignData
      .concat(fitnessData)
      .concat(sleepData)
      .concat(bodyData)
      .concat(healthScoreData),
    loading:
      vitalSignLoading ||
      fitnessLoading ||
      sleepLoading ||
      bodyLoading ||
      healthScoresLoading,
    refetch:
      category === TrackerType.VitalSigns
        ? refetchVitalSign
        : category === TrackerType.Body
          ? refetchBody
          : category === TrackerType.Fitness
            ? refetchFitness
            : category === TrackerType.Sleep
              ? refetchSleep
              : refetchHealthScores,
  };
}

export function useObservations(
  type: string,
  category: TrackerType,
  dateRange?: DateRange
): [
  DataPointMapped[],
  boolean,
  (variables?: {
    input: GetObservationsModelInput;
  }) => Promise<
    ApolloQueryResult<
      | ListVitalSignObservationsQuery
      | ListBodyObservationsQuery
      | ListFitnessObservationsQuery
      | ListHealthScoreObservationsQuery
      | ListSleepObservationsQuery
    >
  >,
  ReferenceRangeModel | undefined,
] {
  const { data: referenceRangeData, loading: referenceRangeLoading } =
    useGetReferenceRanges({
      codes: [type],
      category,
    });
  const {
    data,
    loading: observationsLoading,
    refetch,
  } = useGetObservationsByCategory(category, type, dateRange);
  const {
    state: { conversion },
  } = useConversion();

  const loading = observationsLoading || referenceRangeLoading;
  const results = getVitalSignResults(type, data, conversion);

  return [results, loading, refetch, referenceRangeData.at(0)];
}

export function useCreateVitalSign(): [
  (
    params: PostVitalSignObservationRecordMutationVariables
  ) => Promise<FetchResult<VitalSignObservationRecord>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] =
    usePostVitalSignObservationRecordMutation();

  const loading = baseLoading;

  async function createVitalSign(
    params: PostVitalSignObservationRecordMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
      update: (cache, { data: response }) => {
        if (!response?.postVitalSignObservationRecord) {
          return;
        }
        const storeObject =
          response.postVitalSignObservationRecord as unknown as StoreObject;
        try {
          cache.modify<{ listVitalSignObservations: CachedRecordList }>({
            fields: {
              listVitalSignObservations: (
                existing,
                { storeFieldName, toReference }
              ) => {
                if (isReference(existing)) {
                  return existing;
                }
                const parsed = parseArgs(storeFieldName);
                if (parsed?.patient === params.input.subject?.reference) {
                  return {
                    ...existing,
                    data: [...existing.data, toReference(storeObject)],
                  };
                }
                return existing;
              },
            },
          });
        } catch (e) {
          if (isDevelopment) {
            // eslint-disable-next-line no-console
            console.error(e);
          }
        }
      },
    });
    const createdVitalSign = response.data?.postVitalSignObservationRecord;
    return {
      ...response,
      data: createdVitalSign
        ? new VitalSignObservationRecord(createdVitalSign)
        : undefined,
    };
  }

  return [createVitalSign, loading];
}

export function useUpdateVitalSign(): [
  (
    params: UpdateVitalSignObservationRecordMutationVariables
  ) => Promise<FetchResult<VitalSignObservationRecord>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] =
    useUpdateVitalSignObservationRecordMutation();
  const loading = baseLoading;

  async function updateVitalSign(
    params: UpdateVitalSignObservationRecordMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
    });
    const updatedVitalSign = response.data?.updateVitalSignObservationRecord;
    return {
      ...response,
      data: updatedVitalSign
        ? new VitalSignObservationRecord(updatedVitalSign)
        : undefined,
    };
  }
  return [updateVitalSign, loading];
}

export function useDeleteVitalSign(): [
  (
    params: DeleteVitalSignObservationRecordMutationVariables
  ) => Promise<FetchResult<DeleteVitalSignObservationRecordMutation>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] =
    useDeleteVitalSignObservationRecordMutation();
  const loading = baseLoading;

  async function deleteVitalSign(
    params: DeleteVitalSignObservationRecordMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
      update: (cache, { data: response }) => {
        if (!response?.deleteVitalSignObservationRecord) {
          return;
        }
        const { observation_id: id } = params.input;
        try {
          const normalizedId = cache.identify({
            id,
            __typename: 'ObservationModel',
          });
          cache.evict({ id: normalizedId });
          cache.gc();
        } catch (e) {
          if (isDevelopment) {
            // eslint-disable-next-line no-console
            console.error(e);
          }
        }
      },
    });
    return response;
  }
  return [deleteVitalSign, loading];
}
